import React, {useCallback, useEffect, useRef, useState} from 'react';
import {getCorrectPath} from '../../utils';

type Props = {
  audioUrl: string;
};

function AudioPlayerItem({audioUrl}: Props) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const audioSrc = getCorrectPath(audioUrl, 'audio');

  const setupAudio = useCallback(() => {
    if (!audioSrc || audioRef.current) return;

    const audio = new Audio(audioSrc);
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    });

    audio.addEventListener('durationchange', () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    });

    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
    });

    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
    });
  }, [audioSrc]);

  useEffect(() => {
    setupAudio();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [setupAudio]);

  const togglePlayback = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!audioRef.current) {
      setupAudio();
    }

    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `${m}:${s}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="sm:w-[392px] px-3 py-2 border-[1px] border-[#8080808C] rounded-lg bg-white flex items-center gap-3">
      <button
        onClick={togglePlayback}
        className="w-[36px] h-[36px] rounded-full bg-tealBlue hover:opacity-90 transition-opacity duration-200 flex items-center justify-center flex-shrink-0">
        {isPlaying ? (
          <svg width="12" height="14" viewBox="0 0 12 14" fill="white">
            <rect x="1" y="1" width="3.5" height="12" rx="1" />
            <rect x="7.5" y="1" width="3.5" height="12" rx="1" />
          </svg>
        ) : (
          <svg width="12" height="14" viewBox="0 0 12 14" fill="white">
            <polygon points="1,1 11,7 1,13" />
          </svg>
        )}
      </button>

      <div className="flex-1 flex flex-col gap-1 min-w-0">
        <div className="h-[4px] bg-gray90 rounded-full relative overflow-hidden">
          <div
            className="h-full bg-tealBlue rounded-full transition-[width] duration-100"
            style={{width: `${progressPercent}%`}}
          />
        </div>

        <div className="flex justify-between items-center">
          <span className="text-[11px] text-gray">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default AudioPlayerItem;
